<template>
  <div class="typer-with-header">
    <shared-header />
    <div class="typer-container">
    <!-- 从导航「Typer」进入：可粘贴范文直接练，或留空/填词让 AI 按托福提示生成 -->
    <div v-if="showArticleInput" class="custom-article-step">
      <h3>范文 / 单词（可选）</h3>
      <p class="mode-desc">
        可直接粘贴<strong>整篇英文</strong>用于左侧按钮；或粘贴<strong>单词列表</strong>、也可留空，用右侧按钮按托福范文提示让 AI 生成一篇再练。下方为<strong>练习模式</strong>计时与目标。
      </p>
      <el-input
        v-model="customArticleDraft"
        type="textarea"
        :rows="14"
        placeholder="直接练习：粘贴完整英文文章。AI 生成：可粘贴若干英文单词（逗号/空格分隔）或留空。"
        class="custom-article-textarea"
      />
      <div class="practice-settings">
        <el-form label-width="100px" size="small">
          <el-form-item label="时间限制(分钟)">
            <el-input-number v-model="practiceSettings.timeLimitMinutes" :min="1" :max="60" />
          </el-form-item>
          <el-form-item label="WPM 目标">
            <el-input-number v-model="practiceSettings.wpmTarget" :min="10" :max="200" />
          </el-form-item>
          <el-form-item label="准确率目标(%)">
            <el-input-number v-model="practiceSettings.accuracyTarget" :min="50" :max="100" />
          </el-form-item>
        </el-form>
      </div>
      <div class="custom-article-actions">
        <el-button type="primary" size="large" @click="submitCustomArticle">
          使用上方文本开始练习
        </el-button>
        <el-button type="success" size="large" @click="submitCustomArticleByAI">
          AI 生成范文并开始练习
        </el-button>
      </div>
    </div>
    <div v-else-if="!articleReady" class="loading-article">
      正在生成文章，请稍候…
    </div>
    <!-- 模式选择（文章加载完成后、未开始时显示） -->
    <div v-else-if="showModeSelection" class="mode-selection">
      <h3>选择训练模式</h3>
      <div class="mode-buttons">
        <el-button type="success" @click="setMode('learning')">学习模式</el-button>
        <el-button type="primary" @click="setMode('practice')">练习模式</el-button>
      </div>
      <p class="mode-desc learning-desc" v-if="mode === 'learning'">
        无时间压力，边看边打；实时标红错误；可暂停、查看释义或范文翻译。
      </p>
      <div v-if="mode === 'practice'" class="practice-settings">
        <el-form label-width="100px" size="small">
          <el-form-item label="时间限制(分钟)">
            <el-input-number v-model="practiceSettings.timeLimitMinutes" :min="1" :max="60" />
          </el-form-item>
          <el-form-item label="WPM 目标">
            <el-input-number v-model="practiceSettings.wpmTarget" :min="10" :max="200" />
          </el-form-item>
          <el-form-item label="准确率目标(%)">
            <el-input-number v-model="practiceSettings.accuracyTarget" :min="50" :max="100" />
          </el-form-item>
        </el-form>
      </div>
      <el-button
        v-if="mode"
        type="primary"
        @click="startWithMode"
        style="margin-top: 1em"
        >开始</el-button
      >
    </div>

    <template v-else>
      <div class="mode-switch-bar">
        <el-radio-group
          :model-value="mode"
          size="small"
          @change="switchToMode"
        >
          <el-radio-button label="learning">学习模式</el-radio-button>
          <el-radio-button label="practice">练习模式</el-radio-button>
        </el-radio-group>
        <p class="mode-switch-hint">切换模式会保留当前文章，并从篇首重新开始本遍打字</p>
      </div>
      <!-- 参考区：当前打字行 + 下一行预览；进入下一行时上一行收起（等效向上滚一行） -->
      <div
        ref="typingFocusPanel"
        class="typing-focus-panel"
        @click="onTypingPanelActivate"
      >
        <div class="typing-ref-toolbar">
          <el-button size="small" text type="primary" @click="fullArticleExpanded = !fullArticleExpanded">
            {{ fullArticleExpanded ? '收起全文' : '查看全文' }}
          </el-button>
        </div>
        <div ref="lineMeasure" class="line-measure typing-text" aria-hidden="true">
          <template v-for="(word, idx) in words" :key="'m' + idx">
            <span class="word-block line-measure-word" :data-wi="idx">
              <span>{{ word }}</span>
            </span><span v-if="idx < words.length - 1">&nbsp;</span>
          </template>
        </div>
        <div class="typing-single-line-ref typing-text">
          <div
            v-for="(lineWordIndices, lineIdx) in referenceVisibleLineGroups"
            :key="'rl' + lineIdx + '-' + (lineWordIndices[0] ?? 'x')"
            class="typing-ref-row"
          >
            <span
              v-for="(idx, pos) in lineWordIndices"
              :key="'r' + lineIdx + '-' + idx"
              class="word-block"
            >
              <span
                :class="{
                  'typed-correct':
                    idx < currentWordIndex &&
                    normalizeWordForCompare(typedWords[idx]?.input) === normalizeWordForCompare(words[idx]),
                  'typed-error':
                    idx < currentWordIndex &&
                    normalizeWordForCompare(typedWords[idx]?.input) !== normalizeWordForCompare(words[idx]),
                  'current-word': idx === currentWordIndex,
                  'typed-word': idx > currentWordIndex,
                  'word-clickable': mode === 'learning'
                }"
                @click="mode === 'learning' ? onWordClick(words[idx], idx) : null"
                >{{ words[idx] }}</span
              ><span v-if="pos < lineWordIndices.length - 1">&nbsp;</span>
            </span>
          </div>
        </div>
        <div class="typing-student-output typing-text">
          <div ref="typingStudentScroll" class="typing-student-scroll">
            <!-- 关键点：按“标准文本”的换行分组逐行渲染，避免学生内容长度变化导致自动换行位置漂移 -->
            <div
              v-for="(lineWordIndices, lineIdx) in wordLineGroups"
              :key="'sline' + lineIdx"
              class="typing-ref-row"
            >
              <span
                v-for="(idx, pos) in lineWordIndices"
                :key="'s' + lineIdx + '-' + idx"
                class="word-block"
              >
                <span :class="studentWordSpanClass(idx)">{{ studentWordDisplay(idx) }}</span>
                <span v-if="pos < lineWordIndices.length - 1">&nbsp;</span>
              </span>
            </div>
          </div>
        </div>
        <p class="typing-progress-snippet">{{ correctCount }}/{{ typedWords.length }}</p>
        <div
          v-show="fullArticleExpanded"
          class="typing-full-article typing-text"
        >
          <span
            v-for="(word, idx) in words"
            :key="'f' + idx"
            class="word-block"
          >
            <span
              :class="{
                'typed-correct':
                  idx < currentWordIndex &&
                  normalizeWordForCompare(typedWords[idx]?.input) === normalizeWordForCompare(word),
                'typed-error':
                  idx < currentWordIndex &&
                  normalizeWordForCompare(typedWords[idx]?.input) !== normalizeWordForCompare(word),
                'current-word': idx === currentWordIndex,
                'typed-word': idx > currentWordIndex,
                'word-clickable': mode === 'learning'
              }"
              @click="mode === 'learning' ? onWordClick(word, idx) : null"
              >{{ word }}</span
            ><span v-if="idx < words.length - 1">&nbsp;</span>
          </span>
        </div>
        <div
          v-if="mode === 'learning' && words[currentWordIndex] && !finished"
          class="current-word-chars current-word-chars--inline"
        >
          <span>当前词：</span>
          <span
            v-for="(item, i) in currentWordCharStatus"
            :key="'c' + i"
            :class="'char-' + item.type"
            >{{ item.char }}</span
          >
        </div>
        <!-- 放在面板底部，避免部分浏览器把自动填充/辅助 UI 画在参考区上方 -->
        <input
          ref="typingCaptureInput"
          v-model="userInput"
          type="text"
          class="typing-capture-input"
          :disabled="finished || inputDisabled"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
          aria-label="打字练习输入"
          @input="handleInput"
          @keydown.space.prevent="handleSpaceKeydown"
        />
      </div>
      <div class="timer">
        当前用时：{{ timeUsed }} 秒
        <span v-if="isPracticeMode && countdown > 0"> | 剩余时间：{{ countdown }} 秒</span>
      </div>
      <div
        style="margin-bottom: 1em; display: flex; gap: 0.5em; justify-content: center; flex-wrap: wrap"
      >
        <el-button
          type="warning"
          @click="togglePause"
          :disabled="finished || inputDisabled"
          >{{ paused ? '继续' : '暂停' }}</el-button
        >
        <el-button
          type="primary"
          @click="endPractice"
          :disabled="finished || inputDisabled"
          >结束</el-button
        >
      </div>
      <!-- 学习模式：释义与范文翻译（可折叠，点击才请求 API） -->
      <div v-if="mode === 'learning'" class="learn-extra">
        <el-collapse>
          <el-collapse-item title="查看单词释义 / 范文翻译" name="1">
            <div class="translation-panel">
              <p class="hint">点击上方文章中的单词，可查看该词在语境中的释义（由 AI 生成）</p>
              <div v-if="currentWordForMeaning" class="meaning-block">
                <strong>「{{ currentWordForMeaning }}」释义：</strong>
                <span v-if="wordMeaningLoading" class="loading-inline">加载中…</span>
                <span v-else class="meaning-text">{{ wordMeaningResult || '—' }}</span>
              </div>
              <el-divider />
              <p><strong>翻译整篇文章</strong></p>
              <el-button
                type="primary"
                size="small"
                :loading="articleTranslationLoading"
                @click="fetchArticleTranslation"
              >翻译原文</el-button>
              <div v-if="articleTranslationResult" class="translation-result">
                <pre class="sample-text">{{ articleTranslationResult }}</pre>
              </div>
            </div>
          </el-collapse-item>
        </el-collapse>
      </div>
      <div v-if="finished" class="result">
        <el-button type="primary" @click="restart">再来一次</el-button>
      </div>

      <!-- 报告：练习模式带目标达标分析 -->
      <div v-if="finished" class="report">
        <div>WPM：{{ wpm }}</div>
        <div>准确率：{{ accuracy }}%</div>
        <template v-if="isPracticeMode">
          <div class="target-result">
            <div>WPM 目标：{{ practiceSettings.wpmTarget }} — {{ wpm >= practiceSettings.wpmTarget ? '达标' : '未达标' }}</div>
            <div>准确率目标：{{ practiceSettings.accuracyTarget }}% — {{ Number(accuracy) >= practiceSettings.accuracyTarget ? '达标' : '未达标' }}</div>
          </div>
        </template>
        <div v-if="wrongWords.length">
          <div>打错的单词：</div>
          <ul class="wrong-word-list">
            <li v-for="item in wrongWords" :key="item.index">
              <span class="wrong-col wrong-col-label">原词：<span>{{ item.correct }}</span></span>
              <span class="wrong-col wrong-col-label"
                >拼写：<span class="wrong-word-input">{{ item.input }}</span></span
              >
              <span class="wrong-col wrong-col-label"
                >错误：
                <span class="wrong-word-diff">
                  <span
                    v-for="(token, tokenIdx) in buildWrongWordDiffTokens(item.correct, item.input)"
                    :key="'wd' + item.index + '-' + tokenIdx"
                    :class="['wrong-diff-char', 'wrong-diff-' + token.type]"
                  >
                    <span class="wrong-diff-main-char">{{ token.char }}</span><span v-if="token.type === 'replace'" class="wrong-diff-expected">{{ token.expected }}</span>
                  </span>
                </span>
              </span
              >
            </li>
          </ul>
        </div>
        <div v-else>全部正确！</div>
      </div>
    </template>
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import {
  resolveTyperAiText,
  resolveTyperBoundStudentId,
  unwrapApiData
} from '../../../utils/typerNormalize'
import { buildToeflArticlePrompt } from './toeflArticlePrompt.js'
import SharedHeader from '../../shared/SharedHeader.vue'

export default {
  name: 'Index',
  components: { SharedHeader },
  data () {
    return {
      displayText: 'The quick brown fox jumps over the lazy dog.',
      words: [],
      currentWordIndex: 0,
      userInput: '',
      startTime: null,
      timer: null,
      timeUsed: 0,
      finished: false,
      typedWords: [],
      inputDisabled: true,
      paused: false,
      countdown: 0,
      countdownTimer: null,
      // 双模式
      mode: null, // 'learning' | 'practice'
      showModeSelection: true,
      /** 导航「Typer」入口：先录入文章 */
      showArticleInput: false,
      customArticleDraft: '',
      articleReady: false,
      practiceSettings: {
        timeLimitMinutes: 3,
        wpmTarget: 60,
        accuracyTarget: 95
      },
      // 单词释义 / 文章翻译（DeepSeek，按需请求）
      wordMeaningCache: {},
      currentWordForMeaning: null,
      wordMeaningLoading: false,
      wordMeaningResult: '',
      articleTranslationLoading: false,
      articleTranslationResult: '',
      /** 从「我的作业」进入时由教师布置带入 */
      assignmentTitle: '',
      assignmentCaseSensitive: false,
      /** 作业要求的倒计时（秒）；有值时优先于 practiceSettings 的分钟换算 */
      assignmentCountdownFromAssign: null,
      lastLoadedAssignmentId: null,
      assignmentResultSubmitted: false,
      /** 按当前面板宽度换行后，每行包含的词下标列表（由 line-measure 测量） */
      wordLineGroups: [],
      lineLayoutObserver: null,
      /** 是否展开显示整篇参考范文 */
      fullArticleExpanded: false
    }
  },
  computed: {
    isPracticeMode () {
      return this.mode === 'practice'
    },
    // 学习模式：当前词逐字符状态，用于实时标红（标点须一致；不区分大小写时仅字母忽略大小写）
    currentWordCharStatus () {
      if (this.mode !== 'learning' || this.currentWordIndex >= this.words.length) return []
      const expected = this.words[this.currentWordIndex] || ''
      const typed = this.userInput || ''
      const arr = []
      const maxLen = Math.max(expected.length, typed.length)
      const charMatch = (e, t) => {
        if (e === t) return true
        if (
          !this.assignmentCaseSensitive &&
          e &&
          t &&
          /[a-zA-Z]/.test(e) &&
          /[a-zA-Z]/.test(t)
        ) {
          return e.toLowerCase() === t.toLowerCase()
        }
        return false
      }
      for (let i = 0; i < maxLen; i++) {
        const e = expected[i]
        const t = typed[i]
        if (e && t) {
          arr.push({ char: t, type: charMatch(e, t) ? 'correct' : 'wrong' })
        } else if (e) {
          arr.push({ char: e, type: 'missing' })
        } else {
          arr.push({ char: t, type: 'extra' })
        }
      }
      return arr
    },
    wpm () {
      // 每分钟完成单词数 = 已用空格提交的词数 ÷ 用时（分钟）
      const wordsTyped = this.typedWords.length
      const seconds = Number(this.timeUsed) || 0
      const minutes = seconds / 60
      return minutes > 0 ? Math.round(wordsTyped / minutes) : 0
    },
    accuracy () {
      if (!this.typedWords.length) return 0
      let correct = 0
      for (let i = 0; i < this.words.length; i++) {
        if (
          this.typedWords[i]?.input != null &&
          this.words[i] != null &&
          this.normalizeWordForCompare(this.typedWords[i].input) === this.normalizeWordForCompare(this.words[i])
        ) {
          correct++
        }
      }
      return ((correct / this.words.length) * 100).toFixed(2)
    },
    wrongWords () {
      const arr = []
      for (let i = 0; i < this.words.length; i++) {
        if (
          this.typedWords[i]?.input != null &&
          this.words[i] != null &&
          this.normalizeWordForCompare(this.typedWords[i].input) !== this.normalizeWordForCompare(this.words[i])
        ) {
          arr.push({
            index: i,
            correct: this.words[i],
            input: this.typedWords[i]?.input || ''
          })
        }
      }
      return arr
    },
    correctCount () {
      let count = 0
      for (let i = 0; i < this.typedWords.length; i++) {
        if (
          this.typedWords[i]?.input != null &&
          this.words[i] != null &&
          this.normalizeWordForCompare(this.typedWords[i].input) === this.normalizeWordForCompare(this.words[i])
        ) {
          count++
        }
      }
      return count
    },
    /** 用于决定「当前排版行」：打完则定在最后一句所在行 */
    activeWordIndexForLine () {
      if (!this.words.length) return 0
      // 结束时不要强制切到最后一行：
      // - 如果还没打完（button 结束），就按当前 currentWordIndex 显示停留位置
      // - 如果已打完（currentWordIndex === words.length），则会自然落到最后一词的行
      return Math.min(Math.max(0, this.currentWordIndex), this.words.length - 1)
    },
    /** 当前活跃词在 wordLineGroups 中的行号 */
    currentLineGroupIndex () {
      const groups = this.wordLineGroups
      const cw = this.activeWordIndexForLine
      if (!this.words.length) return 0
      if (!groups || !groups.length) return 0
      for (let i = 0; i < groups.length; i++) {
        if (groups[i].includes(cw)) return i
      }
      return Math.max(0, groups.length - 1)
    },
    /**
     * 标准文本区最多显示 3 行：
     * - 第 1 行时：无上文，显示 [当前, 下文…]（当前在窗口顶部）
     * - 从第 2 行起：始终 [上文一行, 当前行, 下文一行]，当前行固定在中间（第 2 行）
     * - 最后一行且无下文时：仅两行，当前在第二行
     */
    referenceVisibleLineGroups () {
      const groups = this.wordLineGroups
      const cw = this.activeWordIndexForLine
      const li = this.currentLineGroupIndex
      if (!this.words.length) return []
      if (!groups || !groups.length) return [[cw]]
      const n = groups.length
      if (n === 1) return [groups[0]]
      if (li === 0) {
        return groups.slice(0, Math.min(3, n))
      }
      return groups.slice(li - 1, Math.min(n, li + 2))
    }
  },
  watch: {
    $route (to) {
      if (to.path !== '/vocTyper') return
      this.syncRouteToTyperState(to)
    },
    /** 收起全文后版面/滚动条变化会导致 scrollTop 超出新内容高度，学生区看起来像空白 */
    fullArticleExpanded () {
      this.$nextTick(() => {
        this.clampTypingStudentScroll()
        requestAnimationFrame(() => this.clampTypingStudentScroll())
      })
    }
  },
  created () {
    this.syncRouteToTyperState(this.$route)
    // if (this.$route.query.words) {
    //   try {
    //     this.words = JSON.parse(this.$route.query.words)
    //   } catch (e) {
    //     this.words = []
    //   }
    // } else {
    //   this.words = this.displayText.split(' ')
    // }
  },
  methods: {
    buildToeflArticlePrompt,
    /** 与 query 同步：作业 id / 自定义范文 / 默认 AI 范文 */
    syncRouteToTyperState (route) {
      const q = route.query || {}
      if (q.assignmentId) {
        const id = String(q.assignmentId)
        if (id !== this.lastLoadedAssignmentId) {
          this.loadTyperFromAssignment(id)
        }
        return
      }
      if (q.custom === '1') {
        this.clearAssignmentContext()
        this.clearTypingSession()
        this.showArticleInput = true
        this.articleReady = false
        this.showModeSelection = true
        this.mode = null
        this.inputDisabled = true
        return
      }
      this.clearAssignmentContext()
      this.clearTypingSession()
      this.showArticleInput = false
      this.articleReady = false
      this.showModeSelection = true
      this.mode = null
      this.inputDisabled = true
      this.generateTypingTextByAI()
    },
    clearAssignmentContext () {
      this.assignmentTitle = ''
      this.assignmentCaseSensitive = false
      this.assignmentCountdownFromAssign = null
      this.lastLoadedAssignmentId = null
      this.assignmentResultSubmitted = false
    },
    clearTypingSession () {
      this.userInput = ''
      this.currentWordIndex = 0
      this.startTime = null
      this.timeUsed = 0
      this.finished = false
      this.typedWords = []
      this.paused = false
      clearInterval(this.timer)
      clearInterval(this.countdownTimer)
      this.countdownTimer = null
      this.timer = null
      this.wordMeaningCache = {}
      this.currentWordForMeaning = null
      this.wordMeaningResult = ''
      this.articleTranslationResult = ''
      this.fullArticleExpanded = false
      this.assignmentResultSubmitted = false
    },
    loadTyperFromAssignment (assignmentId) {
      if (!localStorage.getItem('userId')) {
        ElMessage.warning('请先登录')
        this.$router.replace('/login')
        return
      }
      this.clearTypingSession()
      this.lastLoadedAssignmentId = String(assignmentId)
      this.inputDisabled = true
      this.articleReady = false
      this.$store
        .dispatch('TyperAssignment/getAssignmentById', { assignmentId })
        .then((response) => {
          const row = unwrapApiData(response) || {}
          this.assignmentTitle = row.title || row.assignmentTitle || ''
          this.assignmentCaseSensitive = !!(row.caseSensitive ?? row.isCaseSensitive)
          const rawCountdown = row.countdown
          this.assignmentCountdownFromAssign = typeof rawCountdown === 'number'
            ? rawCountdown
            : Number(rawCountdown) || null
          this.practiceSettings.wpmTarget = Number(row.wpm) || this.practiceSettings.wpmTarget
          this.practiceSettings.accuracyTarget = Number(row.standardRate) || this.practiceSettings.accuracyTarget
          const text = resolveTyperAiText(row)
          if (!text) {
            ElMessage.error('该作业的 AI 文本为空，无法开始练习')
            this.$router.replace({ path: '/vocTyper/my-assignments' })
            return
          }
          this.displayText = text
          this.words = text.split(/\s+/).filter(Boolean)
          if (!this.words.length) {
            ElMessage.error('该作业的 AI 文本为空，无法开始练习')
            this.$router.replace({ path: '/vocTyper/my-assignments' })
            return
          }
          this.articleReady = true
          this.showArticleInput = false
          this.mode = 'practice'
          this.showModeSelection = false
          this.inputDisabled = false
          this.$nextTick(() => {
            this.startWithMode()
          })
        })
        .catch((err) => {
          const d = err.response?.data
          const msg =
            (typeof d === 'string' && d) ||
            d?.message ||
            d?.title ||
            err.message ||
            '未找到该作业或无权查看'
          ElMessage.error(String(msg))
          this.$router.replace({ path: '/vocTyper/my-assignments' })
        })
    },
    // 整词比较：标点、数字等均须一致；不区分大小写时仅对整串 toLowerCase（标点位置与符号不变）
    normalizeWordForCompare (str) {
      if (str == null || typeof str !== 'string') return ''
      const t = str.trim()
      return this.assignmentCaseSensitive ? t : t.toLowerCase()
    },
    studentWordDisplay (idx) {
      if (idx < this.currentWordIndex) {
        const t = (this.typedWords[idx]?.input ?? '').trim()
        return t || '\u00a0'
      }
      if (idx === this.currentWordIndex) {
        return this.userInput != null && this.userInput !== ''
          ? this.userInput
          : '\u00a0'
      }
      return '\u00a0'
    },
    studentWordSpanClass (idx) {
      if (idx < this.currentWordIndex) {
        const raw = this.typedWords[idx]?.input
        const expected = this.words[idx]
        if (raw == null || String(raw).trim() === '') return 'student-typed-pending'
        const ok =
          this.normalizeWordForCompare(raw) ===
          this.normalizeWordForCompare(expected)
        return ok ? 'student-typed-correct' : 'student-typed-wrong'
      }
      if (idx === this.currentWordIndex) return 'student-typing-current'
      return 'student-typed-pending'
    },
    getContextForWord (idx) {
      const from = Math.max(0, idx - 4)
      const to = Math.min(this.words.length, idx + 5)
      return this.words.slice(from, to).join(' ')
    },
    onWordClick (word, idx) {
      const context = this.getContextForWord(idx)
      const cacheKey = word + '\n' + context
      if (this.wordMeaningCache[cacheKey]) {
        this.currentWordForMeaning = word
        this.wordMeaningResult = this.wordMeaningCache[cacheKey]
        this.focusTypingCapture()
        return
      }
      this.currentWordForMeaning = word
      this.wordMeaningResult = ''
      this.wordMeaningLoading = true
      const userContent = `用一句话给出下面英文单词在该句中的中文释义，只输出释义，不要解释语法或例句。\n单词：${word}\n句子：${context}`
      this.$store
        .dispatch('DeepSeek/getDeepSeekResponse', {
          model: 'glm-5.3',
          messages: [{ role: 'user', content: userContent }]
        })
        .then(response => {
          const text = (response.data?.choices?.[0]?.message?.content || '').trim()
          this.wordMeaningCache[cacheKey] = text
          this.wordMeaningResult = text
          this.wordMeaningLoading = false
          this.focusTypingCapture()
        })
        .catch(() => {
          ElMessage({ message: '获取释义失败', type: 'error' })
          this.wordMeaningLoading = false
          this.focusTypingCapture()
        })
    },
    submitCustomArticle () {
      const text = (this.customArticleDraft || '').trim()
      if (!text) {
        ElMessage({ message: '请先在上方输入要练习的英文文章', type: 'warning' })
        return
      }
      this.displayText = text
      this.words = text.split(/\s+/).filter(Boolean)
      if (!this.words.length) {
        ElMessage({ message: '未能从文本中拆分出单词，请检查内容', type: 'warning' })
        return
      }
      this.articleReady = true
      this.showArticleInput = false
      this.mode = 'practice'
      this.showModeSelection = false
      this.startWithMode()
    },
    submitCustomArticleByAI () {
      const hint = (this.customArticleDraft || '').trim()
      this.showArticleInput = false
      this.inputDisabled = true
      ElMessage({ message: '正在生成文章，请稍等…', type: 'info' })
      const userContent = this.buildToeflArticlePrompt(hint || null)
      this.$store
        .dispatch('DeepSeek/getDeepSeekResponse', {
          model: 'glm-5.3',
          messages: [{ role: 'user', content: userContent }]
        })
        .then(response => {
          const content = (response.data?.choices?.[0]?.message?.content || '').trim()
          if (!content) {
            throw new Error('empty')
          }
          this.displayText = content
          this.words = this.displayText.split(/\s+/).filter(Boolean)
          if (!this.words.length) {
            throw new Error('nowords')
          }
          ElMessage({ message: '文章已生成，开始练习', type: 'success' })
          this.articleReady = true
          this.mode = 'practice'
          this.showModeSelection = false
          this.startWithMode()
        })
        .catch(() => {
          this.showArticleInput = true
          this.articleReady = false
          this.inputDisabled = true
          ElMessage({ message: '生成失败，请重试', type: 'error' })
        })
    },
    fetchArticleTranslation () {
      if (this.articleTranslationResult) return
      this.articleTranslationLoading = true
      const userContent = `将以下英文文章翻译成中文，只输出翻译结果，不要额外说明。\n\n${this.displayText}`
      this.$store
        .dispatch('DeepSeek/getDeepSeekResponse', {
          model: 'glm-5.3',
          messages: [{ role: 'user', content: userContent }]
        })
        .then(response => {
          this.articleTranslationResult = (response.data?.choices?.[0]?.message?.content || '').trim()
          this.articleTranslationLoading = false
        })
        .catch(() => {
          ElMessage({ message: '翻译失败', type: 'error' })
          this.articleTranslationLoading = false
        })
    },
    generateTypingTextByAI () {
      ElMessage({
        message: '正在生成文章，请稍等...',
        type: 'success'
      })

      this.inputDisabled = true // 生成前禁用输入框
      const userContent = this.buildToeflArticlePrompt(this.$route.query.words)
      this.$store
        .dispatch('DeepSeek/getDeepSeekResponse', {
          model: 'glm-5.3',
          messages: [{ role: 'user', content: userContent }]
        })
        .then(response => {
          ElMessage({
            message: '文章已生成，请选择模式并开始',
            type: 'success'
          })
          this.displayText = response.data.choices[0].message.content
          this.words = this.displayText.split(/\s+/).filter(Boolean)
          this.articleReady = true
          this.inputDisabled = true
          // 不再自动开始；等用户选模式后 startWithMode 再开始
        })
        .catch(error => console.log(error.toString()))
    },
    startCountdown () {
      if (this.countdownTimer) clearInterval(this.countdownTimer)
      this.countdownTimer = setInterval(() => {
        if (this.countdown > 0 && !this.finished && !this.paused) {
          this.countdown--
        }
        if (this.countdown <= 0 && !this.finished) {
          this.finalizePractice({
            message: '时间到，练习结束！',
            messageType: 'warning'
          })
        }
      }, 1000)
    },
    startTimer () {
      if (!this.startTime) {
        this.startTime = Date.now()
        this.timer = setInterval(() => {
          if (!this.paused) {
            this.timeUsed = ((Date.now() - this.startTime) / 1000).toFixed(2)
          }
        }, 100)
      }
    },
    startPracticeTimingOnFirstInput () {
      // 练习模式：首次输入才真正启动计时与倒计时
      this.startTimer()
      if (this.mode === 'practice' && !this.countdownTimer) {
        this.startCountdown()
      }
    },
    handleInput () {
      // 只有首次输入字符时才启动计时器（支持一次粘贴多个字符）
      if (!this.startTime && this.userInput.length > 0) {
        this.startPracticeTimingOnFirstInput()
      }
    },
    /** 空格提交当前词（keydown + preventDefault，避免写入空格或页面滚动） */
    handleSpaceKeydown () {
      if (this.finished || this.inputDisabled) return
      if (this.userInput.trim().length > 0) {
        this.typedWords.push({
          input: this.userInput.trim()
        })
        this.currentWordIndex++
        this.userInput = ''
        if (this.currentWordIndex === this.words.length) {
          this.finalizePractice()
        }
      }
    },
    focusTypingCapture () {
      if (this.finished || this.inputDisabled) return
      this.$nextTick(() => {
        const el = this.$refs.typingCaptureInput
        if (!el || typeof el.focus !== 'function') return
        try {
          el.focus({ preventScroll: true })
        } catch (_) {
          el.focus()
        }
      })
    },
    blurTypingCapture () {
      this.$nextTick(() => {
        const el = this.$refs.typingCaptureInput
        if (!el || typeof el.blur !== 'function') return
        el.blur()
      })
    },
    onTypingPanelActivate (e) {
      if (this.finished || this.inputDisabled) return
      const t = e.target
      if (!t || typeof t.closest !== 'function') return
      if (t.closest('button, a, textarea, select, .el-button, .el-radio, .el-radio-button')) return
      this.focusTypingCapture()
    },
    setMode (m) {
      this.mode = m
    },
    /** 练习页内切换学习 / 练习：重置本遍进度，练习模式重新计时 */
    switchToMode (newMode) {
      if (newMode !== 'learning' && newMode !== 'practice') return
      if (newMode === this.mode) return
      this.resetTypingProgress()
      this.mode = newMode
      if (newMode === 'practice') {
        this.startPracticeCountdownIfNeeded()
      } else {
        this.countdown = 0
      }
      this.scheduleLineLayoutRefresh()
      this.$nextTick(() => this.focusTypingCapture())
    },
    resetTypingProgress () {
      this.userInput = ''
      this.currentWordIndex = 0
      this.startTime = null
      this.timeUsed = 0
      this.finished = false
      this.typedWords = []
      this.paused = false
      clearInterval(this.timer)
      clearInterval(this.countdownTimer)
      this.countdownTimer = null
      this.timer = null
      this.inputDisabled = false
      this.assignmentResultSubmitted = false
    },
    startPracticeCountdownIfNeeded () {
      if (this.mode !== 'practice') return
      if (this.assignmentCountdownFromAssign != null) {
        this.countdown = this.assignmentCountdownFromAssign
      } else {
        this.countdown = this.practiceSettings.timeLimitMinutes * 60
      }
    },
    startWithMode () {
      if (!this.mode) return
      this.showModeSelection = false
      this.inputDisabled = false
      if (this.mode === 'practice') {
        if (this.assignmentCountdownFromAssign != null) {
          this.countdown = this.assignmentCountdownFromAssign
        } else {
          this.countdown = this.practiceSettings.timeLimitMinutes * 60
        }
      }
      this.scheduleLineLayoutRefresh()
      this.$nextTick(() => this.focusTypingCapture())
    },
    scheduleLineLayoutRefresh () {
      this.$nextTick(() => {
        this.attachLineLayoutObserver()
        this.recomputeReferenceLines()
      })
    },
    attachLineLayoutObserver () {
      if (typeof ResizeObserver === 'undefined') return
      const panel = this.$refs.typingFocusPanel
      if (!panel) return
      if (this.lineLayoutObserver) {
        this.lineLayoutObserver.disconnect()
      }
      this.lineLayoutObserver = new ResizeObserver(() => {
        this.recomputeReferenceLines()
      })
      this.lineLayoutObserver.observe(panel)
    },
    recomputeReferenceLines () {
      if (!this.articleReady || this.showModeSelection || this.showArticleInput) return
      this.$nextTick(() => {
        const measure = this.$refs.lineMeasure
        if (!measure || !this.words.length) {
          this.wordLineGroups = [this.words.map((_, i) => i)]
          this.clampTypingStudentScroll()
          return
        }
        const tokens = measure.querySelectorAll('.line-measure-word[data-wi]')
        if (!tokens.length) {
          this.wordLineGroups = [this.words.map((_, i) => i)]
          this.clampTypingStudentScroll()
          return
        }
        const lines = []
        let lastTop = null
        let curLine = []
        tokens.forEach((node) => {
          const top = node.offsetTop
          const wi = parseInt(node.getAttribute('data-wi'), 10)
          if (Number.isNaN(wi)) return
          if (lastTop === null) {
            lastTop = top
          } else if (top !== lastTop) {
            if (curLine.length) lines.push(curLine)
            curLine = []
            lastTop = top
          }
          curLine.push(wi)
        })
        if (curLine.length) lines.push(curLine)
        this.wordLineGroups = lines.length ? lines : [this.words.map((_, i) => i)]
        this.clampTypingStudentScroll()
      })
    },
    clampTypingStudentScroll () {
      const el = this.$refs.typingStudentScroll
      if (!el) return
      const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight)
      if (el.scrollTop > maxScroll) {
        el.scrollTop = maxScroll
      }
    },
    togglePause () {
      this.paused = !this.paused
      if (!this.paused) {
        this.focusTypingCapture()
      }
    },
    endPractice () {
      if (this.finished || this.inputDisabled) return
      this.finalizePractice({
        message: '已结束练习',
        messageType: 'info'
      })
    },
    finalizePractice ({ message = '', messageType = 'info' } = {}) {
      this.finished = true
      this.inputDisabled = true
      this.blurTypingCapture()
      if (this.startTime) {
        this.timeUsed = ((Date.now() - this.startTime) / 1000).toFixed(2)
      } else {
        this.timeUsed = Number(this.timeUsed || 0).toFixed(2)
      }
      clearInterval(this.timer)
      clearInterval(this.countdownTimer)
      this.countdownTimer = null
      this.timer = null
      if (message) {
        ElMessage({
          message,
          type: messageType
        })
      }
      this.submitAssignmentResult()
    },
    escapeHtml (text) {
      return String(text ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    buildIncorrectWords () {
      const words = this.wrongWords.map((w) => w.correct).filter(Boolean)
      return Array.from(new Set(words)).join('|')
    },
    buildTypedContentHtml () {
      return this.words
        .map((word, idx) => {
          const typedRaw = (this.typedWords[idx]?.input ?? '').trim()
          if (!typedRaw) {
            return '<span class="student-typed-pending">&nbsp;</span>'
          }
          const ok =
            this.normalizeWordForCompare(typedRaw) ===
            this.normalizeWordForCompare(word)
          const esc = this.escapeHtml(typedRaw)
          if (ok) return `<span class="student-typed-correct">${esc}</span>`
          return `<span class="student-typed-wrong">${esc}</span>`
        })
        .join(' ')
    },
    buildWrongWordDiffTokens (correctWord, inputWord) {
      const expected = String(correctWord ?? '')
      const typed = String(inputWord ?? '')
      const eChars = [...expected]
      const tChars = [...typed]
      const m = eChars.length
      const n = tChars.length
      const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0))
      for (let i = 0; i <= m; i++) dp[i][0] = i
      for (let j = 0; j <= n; j++) dp[0][j] = j
      const charEqual = (a, b) => {
        if (a === b) return true
        if (
          !this.assignmentCaseSensitive &&
          /[a-zA-Z]/.test(a) &&
          /[a-zA-Z]/.test(b)
        ) {
          return a.toLowerCase() === b.toLowerCase()
        }
        return false
      }
      for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
          const same = charEqual(eChars[i - 1], tChars[j - 1])
          if (same) {
            dp[i][j] = dp[i - 1][j - 1]
          } else {
            dp[i][j] = Math.min(
              dp[i - 1][j] + 1, // missing in typed
              dp[i][j - 1] + 1, // extra in typed
              dp[i - 1][j - 1] + 1 // replaced char
            )
          }
        }
      }
      const tokens = []
      let i = m
      let j = n
      while (i > 0 || j > 0) {
        if (
          i > 0 &&
          j > 0 &&
          charEqual(eChars[i - 1], tChars[j - 1]) &&
          dp[i][j] === dp[i - 1][j - 1]
        ) {
          tokens.push({ char: tChars[j - 1], type: 'correct' })
          i--
          j--
          continue
        }
        if (
          i > 0 &&
          j > 0 &&
          dp[i][j] === dp[i - 1][j - 1] + 1
        ) {
          tokens.push({
            char: tChars[j - 1],
            expected: eChars[i - 1],
            type: 'replace'
          })
          i--
          j--
          continue
        }
        if (j > 0 && dp[i][j] === dp[i][j - 1] + 1) {
          tokens.push({ char: tChars[j - 1], type: 'extra' })
          j--
          continue
        }
        if (i > 0 && dp[i][j] === dp[i - 1][j] + 1) {
          tokens.push({ char: eChars[i - 1], type: 'missing' })
          i--
        }
      }
      return tokens.reverse()
    },
    submitAssignmentResult () {
      const assignmentId = Number(this.lastLoadedAssignmentId || this.$route.query?.assignmentId)
      if (!Number.isFinite(assignmentId) || assignmentId <= 0) return
      if (this.assignmentResultSubmitted) return
      const typedCount = this.typedWords.length
      const timeUsed = this.startTime
        ? Math.max(0, Math.round((Date.now() - this.startTime) / 1000))
        : 0
      this.timeUsed = Number(timeUsed).toFixed(2)
      const currentWpm = Number(this.wpm) || 0
      const currentAccuracy = Number(this.accuracy) || 0
      const targetWpm = Number(this.practiceSettings.wpmTarget) || 0
      const targetAccuracy = Number(this.practiceSettings.accuracyTarget) || 0
      const passed = currentWpm >= targetWpm && currentAccuracy >= targetAccuracy
      const finished = this.currentWordIndex >= this.words.length
      const incorrectWords = this.buildIncorrectWords()
      const typedContent = this.buildTypedContentHtml()
      const studentId = resolveTyperBoundStudentId()
      if (!studentId) {
        ElMessage.warning('未找到绑定的学生（targetId），无法提交结果')
        return
      }
      const payload = {
        assignmentId,
        studentId,
        wpm: Math.max(0, Math.round(currentWpm)),
        typedCount: Math.max(0, Math.round(typedCount)),
        timeUsed,
        passed: !!passed,
        finished: !!finished,
        incorrectWords,
        typedContent
      }
      this.assignmentResultSubmitted = true
      this.$store
        .dispatch('TyperAssignmentResult/add', payload)
        .then(() => {
          ElMessage.success('练习结果已提交')
        })
        .catch((err) => {
          this.assignmentResultSubmitted = false
          console.error(err)
          const d = err.response?.data
          const firstValidationError = d?.errors
            ? Object.values(d.errors).find((arr) => Array.isArray(arr) && arr.length)?.[0]
            : ''
          const msg =
            (typeof d === 'string' && d) ||
            firstValidationError ||
            d?.message ||
            d?.title ||
            err.message ||
            '练习结果上报失败'
          ElMessage.error(String(msg))
        })
    },
    restart () {
      this.resetTypingProgress()
      this.startPracticeCountdownIfNeeded()
      this.scheduleLineLayoutRefresh()
      this.$nextTick(() => this.focusTypingCapture())
    }
  },
  beforeUnmount () {
    clearInterval(this.timer)
    clearInterval(this.countdownTimer)
    if (this.lineLayoutObserver) {
      this.lineLayoutObserver.disconnect()
      this.lineLayoutObserver = null
    }
  }
}
</script>

<style scoped>
.typer-with-header {
  text-align: left;
}

.typer-with-header .typer-container {
  margin: 24px auto;
}

.typer-container {
  max-width: 80%;
  margin: 100px auto;
  padding: 2em;
  background: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 8px #ccc;
  text-align: center;
}
.typing-focus-panel {
  position: relative;
  margin-bottom: 1em;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  text-align: left;
}
.typing-ref-toolbar {
  display: flex;
  justify-content: flex-end;
  padding: 0.35em 0.5em 0;
  border-bottom: 1px solid #f0f0f0;
}
.typing-full-article {
  max-height: min(40vh, 360px);
  overflow: auto;
  padding: 0.65em 1.1em 0.85em;
  border-top: 1px dashed #e0e0e0;
  white-space: normal;
  word-break: break-word;
  line-height: 1.6em;
}
/* 与可见参考区同宽，用于测量浏览器实际换行（不可见） */
.line-measure {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  visibility: hidden;
  opacity: 0;
  color: transparent;
  pointer-events: none;
  z-index: -1;
  padding: 0.85em 1.1em;
  overflow: visible;
  white-space: normal;
  word-break: break-word;
}
/* 当前打字行 + 下一行预览；换行后上一行不再显示 */
.typing-single-line-ref {
  position: relative;
  z-index: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0.85em 1.1em;
  min-height: auto;
  overflow: visible;
}
.typing-student-output {
  border-top: 1px solid #ececec;
  background: #fbfcfe;
  padding: 0.45em 1.1em 0.75em;
}
.typing-student-scroll {
  max-height: min(42vh, 380px);
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0.25em;
  min-height: 1.35em;
}
.student-typed-correct {
  color: deepskyblue;
}
.student-typed-wrong {
  color: #f44336;
}
.student-typing-current {
  color: orange;
}
.student-typed-pending {
  color: #c8c8c8;
  user-select: none;
}
.typing-ref-row {
  white-space: normal;
  word-break: break-word;
  line-height: 1.6em;
  overflow: visible;
}
/* 仅用于接收键盘，不可见；保留 v-model 与原生编辑行为（粘贴、退格等） */
.typing-capture-input {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
  opacity: 0;
  pointer-events: none;
}
.typing-progress-snippet {
  font-size: 0.85em;
  color: #666;
  margin: 0 0 0.35em;
  padding: 0 1.1em;
  text-align: left;
}
.typing-text {
  font-size: 1.2em;
  margin-bottom: 0.5em;
  color: #333;
  word-spacing: 0.1em;
  white-space: normal; /* 允许自动换行 */
  word-break: break-word; /* 单词太长时也能换行 */
  line-height: 1.6em; /* 可选，增加行高更易读 */
}
/* 每个词作为一组不换行 */
.typing-text .word-block {
  white-space: nowrap;
}
.typed-correct {
  color: deepskyblue;
}
.typed-error {
  color: #f44336;
}
.current-word {
  color: orange;
  /* text-decoration: underline; */
}
.typed-word {
  color: #bbb;
}
.timer {
  font-size: 1em;
  color: #1976d2;
  margin-bottom: 1em;
}
.result {
  margin-top: 1em;
  font-size: 1em;
  color: #444;
}
.report {
  margin-top: 2em;
  text-align: left;
  font-size: 1em;
  background: #f5f5f5;
  padding: 1em;
  border-radius: 6px;
}
.report ul {
  margin: 0.5em 0 0 1em;
  padding: 0;
}
.wrong-word-list {
  list-style: none;
  margin: 0.5em 0 0;
  padding: 0;
}
.wrong-word-list li {
  display: flex;
  align-items: baseline;
  justify-content: flex-start;
  gap: 1.25em;
  text-align: left;
  margin: 0.35em 0;
  flex-wrap: wrap;
}
.wrong-col {
  display: inline-flex;
  align-items: baseline;
  min-width: 180px;
}
.wrong-col-label {
  color: #333;
}
.wrong-word-input {
  color: #f44336;
}
.wrong-word-diff {
  font-family: monospace;
}
.wrong-diff-char {
  display: inline-block;
  min-width: 0.62em;
  text-align: center;
}
.wrong-diff-main-char {
  display: inline;
}
.wrong-diff-correct {
  color: #333;
}
.wrong-diff-replace {
  color: #f44336;
  text-decoration: underline;
  text-decoration-thickness: 2px;
  position: relative;
  padding-right: 0.05em;
}
.wrong-diff-extra {
  color: #ff9800;
  text-decoration: line-through;
}
.wrong-diff-expected {
  display: inline-block;
  margin-left: 0.08em;
  color: #1976d2;
  font-size: 0.78em;
  vertical-align: super;
  line-height: 1;
  text-decoration: none;
}
.wrong-diff-missing {
  color: #1976d2;
  text-decoration: none;
  background: transparent;
  border-radius: 2px;
  min-width: auto;
}
.wrong-diff-missing::before {
  content: '[';
}
.wrong-diff-missing::after {
  content: ']';
}
.wrong-word-marker {
  font-family: monospace;
  letter-spacing: 0.04em;
}
.typing-stat {
  position: fixed;
  right: 2vw;
  top: 2vw;
  background: #fff;
  color: #1976d2;
  font-size: 1.1em;
  font-weight: bold;
  border-radius: 20px;
  box-shadow: 0 2px 8px #ccc;
  padding: 0.5em 1.2em;
  z-index: 99;
}

.loading-article {
  padding: 3em;
  text-align: center;
  color: #666;
}
.custom-article-step {
  padding: 2em;
  text-align: center;
}
.custom-article-step h3 {
  margin-bottom: 0.5em;
}
.custom-article-textarea {
  max-width: 720px;
  margin: 1em auto;
  text-align: left;
  display: block;
}
.custom-article-actions {
  display: flex;
  gap: 1em;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 1em;
}
.mode-selection {
  padding: 2em;
  text-align: center;
}
.mode-selection h3 {
  margin-bottom: 1em;
}
.mode-buttons {
  display: flex;
  gap: 1em;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 1em;
}
.mode-desc {
  color: #666;
  font-size: 0.95em;
  margin: 0.5em 0;
}
.practice-settings {
  text-align: left;
  max-width: 280px;
  margin: 1em auto;
}
.mode-switch-bar {
  margin-bottom: 0.75em;
}
.mode-switch-hint {
  margin: 0.5em 0 0;
  font-size: 0.82em;
  color: #888;
  line-height: 1.4;
}
.mode-badge {
  font-size: 0.9em;
  color: #1976d2;
  margin-bottom: 0.5em;
}
.current-word-chars {
  margin-bottom: 0.5em;
  font-family: monospace;
  font-size: 1.1em;
  min-height: 1.4em;
}
.current-word-chars--inline {
  padding: 0 1.1em 0.35em;
  margin-bottom: 0;
}
.current-word-chars .char-correct {
  color: #4caf50;
}
.current-word-chars .char-wrong,
.current-word-chars .char-extra {
  color: #f44336;
  text-decoration: underline;
}
.current-word-chars .char-missing {
  color: #999;
  text-decoration: underline;
}
.word-clickable {
  cursor: pointer;
}
.word-clickable:hover {
  text-decoration: underline;
}
.learn-extra {
  margin-top: 1em;
  text-align: left;
}
.translation-panel {
  padding: 0.5em 0;
}
.translation-panel .hint {
  color: #666;
  font-size: 0.9em;
  margin-bottom: 0.5em;
}
.meaning-block {
  margin: 0.5em 0;
}
.loading-inline {
  color: #999;
  font-size: 0.9em;
}
.meaning-text {
  color: #333;
}
.translation-result {
  margin-top: 0.75em;
}
.translation-panel .sample-text {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.9em;
  max-height: 200px;
  overflow-y: auto;
  background: #f5f5f5;
  padding: 0.5em;
  border-radius: 4px;
}
.target-result {
  margin-top: 0.5em;
  padding: 0.5em 0;
  border-top: 1px solid #eee;
}
.target-result div {
  margin: 0.2em 0;
}
</style>
